import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import './TestRunDetail.css';

const STATUS_OPTIONS = ['not_started', 'in_progress', 'passed', 'failed', 'blocked', 'skipped'];

const STATUS_META = {
  not_started: { label: 'Not Run',     color: '#6c757d', bg: '#f8f9fa',  icon: '○' },
  in_progress:  { label: 'In Progress', color: '#0d6efd', bg: '#e7f1ff',  icon: '▶' },
  passed:       { label: 'Passed',      color: '#16a34a', bg: '#dcfce7',  icon: '✓' },
  failed:       { label: 'Failed',      color: '#dc2626', bg: '#fee2e2',  icon: '✕' },
  blocked:      { label: 'Blocked',     color: '#d97706', bg: '#fef3c7',  icon: '⊘' },
  skipped:      { label: 'Skipped',     color: '#6c757d', bg: '#f1f5f9',  icon: '»' },
  completed:    { label: 'Completed',   color: '#16a34a', bg: '#dcfce7',  icon: '✓' },
  aborted:      { label: 'Aborted',     color: '#111827', bg: '#f3f4f6',  icon: '✕' },
};

function StatusBadge({ status, size = 'sm' }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: size === 'lg' ? '5px 12px' : '3px 8px',
      borderRadius: 20,
      background: m.bg, color: m.color,
      fontSize: size === 'lg' ? '0.85rem' : '0.75rem',
      fontWeight: 600,
      border: `1px solid ${m.color}33`,
      whiteSpace: 'nowrap',
    }}>
      <span>{m.icon}</span>
      {m.label}
    </span>
  );
}

export default function TestRunDetail() {
  const { projectKey, runId } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Selected test case index for right panel
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Edit state for selected result
  const [editStatus, setEditStatus] = useState('not_started');
  const [editComment, setEditComment] = useState('');
  const [editBugId, setEditBugId] = useState('');

  // Project (for JIRA config)
  const [project, setProject] = useState(null);

  // Resizable split pane
  const [leftWidth, setLeftWidth] = useState(320);
  const isResizing = useRef(false);
  const splitRef = useRef(null);

  // Edit Run modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRunData, setEditRunData] = useState({});
  const [editRunSaving, setEditRunSaving] = useState(false);
  const [editRunError, setEditRunError] = useState('');
  const [editRunUsers, setEditRunUsers] = useState([]);

  // Current user role
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const canEditRun = ['admin', 'qa_manager', 'qa_lead'].includes(currentUser.role);

  // Collapsed folder nodes in tree
  const [collapsedFolders, setCollapsedFolders] = useState({});

  // Collapsible right-panel sections; open by default
  const [openSections, setOpenSections] = useState({ description: true, precondition: true, steps: true, expected: true, result: true });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const startResize = useCallback((e) => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startW = leftWidth;
    const onMove = (ev) => {
      if (!isResizing.current) return;
      const delta = ev.clientX - startX;
      const containerW = splitRef.current?.offsetWidth || 1200;
      const newW = Math.max(200, Math.min(containerW - 300, startW + delta));
      setLeftWidth(newW);
    };
    const onUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [leftWidth]);

  const toggleFolder = (key) => setCollapsedFolders(prev => ({ ...prev, [key]: !prev[key] }));

  // Build grouped tree from results
  const buildTree = useCallback((results) => {
    const groups = {};
    results.forEach((result, idx) => {
      const tc = result.testCaseId;
      if (typeof tc !== 'object' || !tc) {
        // testCaseId not populated at all — ungrouped
        const key = '__ungrouped__';
        if (!groups[key]) groups[key] = [];
        groups[key].push({ result, idx });
        return;
      }

      // Resolve folder name — folderId may be populated object or bare ObjectId string
      let folderName = null;
      if (tc.folderId) {
        if (typeof tc.folderId === 'object' && tc.folderId.name) {
          folderName = tc.folderId.name;
        } else {
          // folderId exists but wasn't populated (deleted folder or ref mismatch)
          folderName = '__unknown_folder__';
        }
      }

      // Fall back to suite name, then ungrouped
      const suiteName = tc.suiteId
        ? (typeof tc.suiteId === 'object' ? tc.suiteId.name : null)
        : null;

      const groupKey = folderName || suiteName || '__ungrouped__';
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push({ result, idx });
    });
    return groups;
  }, []);

  const fetchRun = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/runs/${runId}`);
      setRun(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load test run: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  const fetchProject = useCallback(async () => {
    if (!projectKey) return;
    try {
      const res = await api.get(`/projects/${projectKey}`);
      setProject(res.data?.data || res.data || null);
    } catch { /* optional */ }
  }, [projectKey]);

  useEffect(() => { fetchRun(); fetchProject(); }, [fetchRun, fetchProject]);

  // Pre-fetch users so the assignee dropdown is populated when the modal opens
  useEffect(() => {
    api.get('/auth/users').then(r => setEditRunUsers(r.data.data || r.data.users || [])).catch(() => {});
  }, []);

  // When a row is selected, populate edit fields from existing result
  useEffect(() => {
    if (selectedIdx === null || !run?.results) return;
    const r = run.results[selectedIdx];
    if (r) {
      setEditStatus(r.status || 'not_started');
      setEditComment(r.comment || '');
      setEditBugId(r.bugId || r.jiraIssueKey || '');
      // Re-open all sections for each new TC
      setOpenSections({ description: true, precondition: true, steps: true, expected: true, result: true });
    }
  }, [selectedIdx, run]);

  const openEditRunModal = () => {
    const cfg = run?.configuration || {};
    // Normalize assignedTo: may be an object (populated) or a plain ID string
    const assignedId = run?.assignedTo?._id || run?.assignedTo?.id || run?.assignedTo || '';
    setEditRunData({
      name: run?.name || '',
      description: run?.description || '',
      assignedTo: String(assignedId),
      platform: cfg.platform || '',
      environment: cfg.environment || '',
      deviceName: cfg.deviceName || '',
      osVersion: cfg.osVersion || '',
      appVersion: cfg.appVersion || '',
      buildNumber: cfg.buildNumber || '',
      browserName: cfg.browserName || '',
      browserVersion: cfg.browserVersion || '',
      notes: cfg.notes || '',
    });
    setEditRunError('');
    setShowEditModal(true);
  };

  const saveEditRun = async () => {
    if (!editRunData.name?.trim()) { setEditRunError('Run name is required.'); return; }
    setEditRunSaving(true);
    setEditRunError('');
    try {
      await api.put(`/runs/${runId}`, {
        name: editRunData.name.trim(),
        description: editRunData.description,
        assignedTo: editRunData.assignedTo || undefined,
        configuration: {
          platform: editRunData.platform,
          environment: editRunData.environment,
          deviceName: editRunData.deviceName,
          osVersion: editRunData.osVersion,
          appVersion: editRunData.appVersion,
          buildNumber: editRunData.buildNumber,
          browserName: editRunData.browserName,
          browserVersion: editRunData.browserVersion,
          notes: editRunData.notes,
        },
      });
      await fetchRun();
      setShowEditModal(false);
    } catch (err) {
      setEditRunError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setEditRunSaving(false);
    }
  };

  const handleStartRun = async () => {
    try {
      await api.post(`/runs/${runId}/start`);
      fetchRun();
    } catch (err) {
      setError('Failed to start run: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCompleteRun = async () => {
    if (!window.confirm('Mark this run as completed?')) return;
    try {
      await api.post(`/runs/${runId}/complete`);
      fetchRun();
    } catch (err) {
      setError('Failed to complete run: ' + (err.response?.data?.error || err.message));
    }
  };

  const saveResult = async () => {
    if (selectedIdx === null) return;
    setSaving(true);
    try {
      await api.put(`/runs/${runId}/results/${selectedIdx}`, {
        status: editStatus,
        comment: editComment,
        bugId: editBugId || null,
      });
      await fetchRun();
      // Auto-advance to next not_started
      const results = run?.results || [];
      const next = results.findIndex((r, i) => i > selectedIdx && r.status === 'not_started');
      if (next !== -1) setSelectedIdx(next);
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getBugUrl = (bugId) => {
    if (!bugId) return null;
    const jiraUrl = project?.jiraIntegration?.jiraUrl;
    if (jiraUrl) return `${jiraUrl.replace(/\/$/, '')}/browse/${bugId}`;
    if (/^https?:\/\//.test(bugId)) return bugId;
    return null;
  };

  const getProgress = () => {
    if (!run?.results?.length) return { total: 0, done: 0, passed: 0, failed: 0, blocked: 0, skipped: 0, pct: 0 };
    const total   = run.results.length;
    const passed  = run.results.filter(r => r.status === 'passed').length;
    const failed  = run.results.filter(r => r.status === 'failed').length;
    const blocked = run.results.filter(r => r.status === 'blocked').length;
    const skipped = run.results.filter(r => r.status === 'skipped').length;
    const done    = passed + failed + blocked + skipped;
    return { total, done, passed, failed, blocked, skipped, pct: Math.round((done / total) * 100) };
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error && !run) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
      </div>
    );
  }

  const progress = getProgress();
  const cfg = run?.configuration || {};
  const selectedResult = selectedIdx !== null ? run?.results?.[selectedIdx] : null;
  const selectedTC = selectedResult
    ? (typeof selectedResult.testCaseId === 'object' ? selectedResult.testCaseId : null)
    : null;
  const canEdit = run?.runType === 'manual' && run?.status !== 'aborted' && run?.status !== 'completed';

  return (
    <div className="trd-root">
      {/* ── Top header ── */}
      <div className="trd-header">
        <div className="trd-header-left">
          <nav className="trd-breadcrumb">
            <Link to="/projects">Projects</Link>
            {projectKey && <><span>/</span><Link to={`/projects/${projectKey}`}>{project?.key?.toUpperCase() || project?.name || projectKey}</Link></>}
            <span>/</span>
            <Link to={projectKey ? `/projects/${projectKey}/runs` : '/runs'}>Test Runs</Link>
            <span>/</span>
            <span className="trd-breadcrumb-current">{run?.name}</span>
          </nav>
          <div className="trd-title-row">
            <h1 className="trd-title">{run?.name}</h1>
            <StatusBadge status={run?.status} size="lg" />
            <span className={`trd-run-type-badge ${run?.runType}`}>
              {run?.runType === 'automated' ? '🤖 Automated' : '👤 Manual'}
            </span>
          </div>
          {run?.description && <p className="trd-subtitle">{run.description}</p>}
        </div>
        <div className="trd-header-actions">
          {run?.status === 'not_started' && (
            <button className="btn btn-primary btn-sm" onClick={handleStartRun}>▶ Start Run</button>
          )}
          {run?.status === 'in_progress' && (
            <button className="btn btn-success btn-sm" onClick={handleCompleteRun}>✓ Close Run</button>
          )}
          <Link className="btn btn-outline-secondary btn-sm" to={projectKey ? `/projects/${projectKey}/runs/${runId}/results` : `/runs/${runId}/results`}>🧪 Results</Link>
          {canEditRun && (
            <button className="btn btn-outline-primary btn-sm" onClick={openEditRunModal}>✏️ Edit Run</button>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2">
          <div className="alert alert-danger alert-dismissible py-2 mb-0">
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')} />
          </div>
        </div>
      )}

      {/* ── Summary bar ── */}
      <div className="trd-summary-bar">
        <div className="trd-progress-wrap">
          <div className="trd-progress-bar-track">
            <div className="trd-progress-bar-fill trd-pb-pass"
              style={{ width: `${(progress.passed / (progress.total || 1)) * 100}%` }} />
            <div className="trd-progress-bar-fill trd-pb-fail"
              style={{ width: `${(progress.failed / (progress.total || 1)) * 100}%` }} />
            <div className="trd-progress-bar-fill trd-pb-block"
              style={{ width: `${(progress.blocked / (progress.total || 1)) * 100}%` }} />
            <div className="trd-progress-bar-fill trd-pb-skip"
              style={{ width: `${(progress.skipped / (progress.total || 1)) * 100}%` }} />
          </div>
          <span className="trd-progress-pct">{progress.pct}%</span>
        </div>
        <div className="trd-summary-stats">
          <div className="trd-stat"><span className="trd-stat-num">{progress.total}</span><span className="trd-stat-lbl">Total</span></div>
          <div className="trd-stat trd-stat-pass"><span className="trd-stat-num">{progress.passed}</span><span className="trd-stat-lbl">Passed</span></div>
          <div className="trd-stat trd-stat-fail"><span className="trd-stat-num">{progress.failed}</span><span className="trd-stat-lbl">Failed</span></div>
          <div className="trd-stat trd-stat-block"><span className="trd-stat-num">{progress.blocked}</span><span className="trd-stat-lbl">Blocked</span></div>
          <div className="trd-stat"><span className="trd-stat-num">{progress.total - progress.done}</span><span className="trd-stat-lbl">Not Run</span></div>
        </div>
        <div className="trd-config-pills">
          {cfg.platform && <span className="trd-pill">📱 {cfg.platform}</span>}
          {cfg.environment && <span className="trd-pill">🌐 {cfg.environment}</span>}
          {cfg.appVersion && <span className="trd-pill">📦 v{cfg.appVersion}</span>}
          {cfg.osVersion && <span className="trd-pill">⚙️ {cfg.osVersion}</span>}
          {run?.createdBy?.username && <span className="trd-pill">👤 {run.createdBy.username}</span>}
          {run?.assignedTo && (
            <span className="trd-pill">🎯 {run.assignedTo?.username || editRunUsers.find(u => (u._id || u.id) === String(run.assignedTo))?.username || 'Assigned'}</span>
          )}
        </div>
      </div>

      {/* ── Split pane ── */}
      <div className="trd-split" ref={splitRef}>
        {/* LEFT: folder tree + test case list */}
        <div className="trd-list-panel" style={{ width: leftWidth, minWidth: 200 }}>
          <div className="trd-list-header">
            <span className="trd-list-title">Test Cases</span>
            <span className="trd-list-count">{progress.done}/{progress.total} done</span>
          </div>
          <div className="trd-list-body">
            {!run?.results?.length ? (
              <div className="trd-list-empty">No test cases in this run.</div>
            ) : (() => {
              const tree = buildTree(run.results);
              const groupKeys = Object.keys(tree);
              // Named groups = anything that's not __ungrouped__
              const namedKeys = groupKeys.filter(k => k !== '__ungrouped__' && k !== '__unknown_folder__');
              const hasGroups = namedKeys.length > 0;
              // Order: named folders first (sorted), then unknown, then ungrouped
              const orderedKeys = [
                ...namedKeys.sort(),
                ...(tree['__unknown_folder__'] ? ['__unknown_folder__'] : []),
                ...(tree['__ungrouped__'] ? ['__ungrouped__'] : []),
              ];
              return orderedKeys.map(groupKey => {
                const items = tree[groupKey];
                const isUngrouped = groupKey === '__ungrouped__';
                const isUnknown = groupKey === '__unknown_folder__';
                const isCollapsed = collapsedFolders[groupKey];
                // Show a folder header for: named groups always, unknown/ungrouped only when other groups exist
                const showHeader = !isUngrouped && !isUnknown
                  ? true
                  : hasGroups; // show "No Folder" / "Unknown Folder" header when mixed
                const folderLabel = isUnknown ? 'Unknown Folder' : isUngrouped ? 'No Folder' : groupKey;
                const folderIcon = isUnknown || isUngrouped ? '📂' : '📁';
                return (
                  <div key={groupKey} className="trd-tree-group">
                    {showHeader && (
                      <div
                        className={`trd-tree-folder ${isUngrouped || isUnknown ? 'trd-tree-folder-dim' : ''}`}
                        onClick={() => toggleFolder(groupKey)}
                      >
                        <span className="trd-tree-folder-arrow">{isCollapsed ? '▶' : '▼'}</span>
                        <span className="trd-tree-folder-icon">{folderIcon}</span>
                        <span className="trd-tree-folder-name">{folderLabel}</span>
                        <span className="trd-tree-folder-count">{items.length}</span>
                      </div>
                    )}
                    {/* Test case rows */}
                    {!isCollapsed && items.map(({ result, idx }) => {
                      const tc = result.testCaseId;
                      const title = typeof tc === 'object'
                        ? (tc?.title || tc?.testCaseId || `TC #${idx + 1}`)
                        : `TC #${idx + 1}`;
                      const tcId = typeof tc === 'object' ? tc?.testCaseId : null;
                      const m = STATUS_META[result.status] || STATUS_META.not_started;
                      const isSelected = selectedIdx === idx;
                      const bugId = result.bugId || result.jiraIssueKey;
                      return (
                        <div
                          key={idx}
                          className={`trd-list-item ${isSelected ? 'selected' : ''} ${showHeader ? 'indented' : ''}`}
                          onClick={() => setSelectedIdx(idx)}
                        >
                          <span className="trd-list-item-icon" style={{ color: m.color }}>{m.icon}</span>
                          <div className="trd-list-item-info">
                            {tcId && <span className="trd-list-item-tcid">{tcId}</span>}
                            <span className="trd-list-item-title">{title}</span>
                            {bugId && <span className="trd-list-item-bug">🐛 {bugId}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Drag handle */}
        <div className="trd-resize-handle" onMouseDown={startResize} title="Drag to resize" />

        {/* RIGHT: detail panel */}
        <div className="trd-detail-panel">
          {selectedIdx === null ? (
            <div className="trd-detail-empty">
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>👈</div>
              <p>Select a test case from the list to view its details and record a result.</p>
            </div>
          ) : (
            <div className="trd-detail-content">
              {/* Nav + title */}
              <div className="trd-detail-tc-header">
                <div className="trd-detail-tc-nav">
                  <button className="btn btn-sm btn-outline-secondary"
                    disabled={selectedIdx === 0}
                    onClick={() => setSelectedIdx(i => i - 1)}>‹ Prev</button>
                  <span className="trd-detail-tc-counter">{selectedIdx + 1} / {run.results.length}</span>
                  <button className="btn btn-sm btn-outline-secondary"
                    disabled={selectedIdx === run.results.length - 1}
                    onClick={() => setSelectedIdx(i => i + 1)}>Next ›</button>
                </div>
                <div className="trd-detail-tc-title-row">
                  {selectedTC?.testCaseId && (
                    <span className="trd-detail-tc-id">{selectedTC.testCaseId}</span>
                  )}
                  <h2 className="trd-detail-tc-name">{selectedTC?.title || `Test Case #${selectedIdx + 1}`}</h2>
                  {selectedTC?.priority && (
                    <span className={`badge ${selectedTC.priority === 'High' ? 'bg-danger' : selectedTC.priority === 'Medium' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                      {selectedTC.priority}
                    </span>
                  )}
                </div>
              </div>

              {/* Description — always show if present, collapsible */}
              {selectedTC?.description && (
                <div className="trd-detail-section">
                  <button className="trd-section-toggle" onClick={() => toggleSection('description')}>
                    <span className="trd-section-toggle-arrow">{openSections.description ? '▼' : '▶'}</span>
                    <span className="trd-section-toggle-label">DESCRIPTION</span>
                  </button>
                  {openSections.description && (
                    <p className="trd-detail-section-body">{selectedTC.description}</p>
                  )}
                </div>
              )}

              {/* Precondition — always visible (blank if empty), collapsible */}
              <div className="trd-detail-section">
                <button className="trd-section-toggle" onClick={() => toggleSection('precondition')}>
                  <span className="trd-section-toggle-arrow">{openSections.precondition ? '▼' : '▶'}</span>
                  <span className="trd-section-toggle-label">PRECONDITION</span>
                </button>
                {openSections.precondition && (
                  selectedTC?.precondition
                    ? <p className="trd-detail-section-body">{selectedTC.precondition}</p>
                    : <p className="trd-detail-section-body trd-detail-section-empty">No precondition specified.</p>
                )}
              </div>

              {/* Steps — collapsible */}
              {selectedTC?.steps?.length > 0 && (
                <div className="trd-detail-section">
                  <button className="trd-section-toggle" onClick={() => toggleSection('steps')}>
                    <span className="trd-section-toggle-arrow">{openSections.steps ? '▼' : '▶'}</span>
                    <span className="trd-section-toggle-label">STEPS</span>
                    <span className="trd-section-toggle-count">{selectedTC.steps.length}</span>
                  </button>
                  {openSections.steps && (
                    <div className="trd-steps">
                      {selectedTC.steps.map((step, si) => (
                        <div key={si} className="trd-step">
                          <span className="trd-step-num">{si + 1}</span>
                          <div className="trd-step-body">
                            <div className="trd-step-action">
                              {typeof step === 'object'
                                ? (step.action || step.step || step.description || JSON.stringify(step))
                                : step}
                            </div>
                            {typeof step === 'object' && step.expectedResult && (
                              <div className="trd-step-expected">
                                <span className="trd-step-expected-label">Expected:</span>
                                {step.expectedResult}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Expected Result — collapsible */}
              {selectedTC?.expectedResult && (
                <div className="trd-detail-section">
                  <button className="trd-section-toggle" onClick={() => toggleSection('expected')}>
                    <span className="trd-section-toggle-arrow">{openSections.expected ? '▼' : '▶'}</span>
                    <span className="trd-section-toggle-label">EXPECTED RESULT</span>
                  </button>
                  {openSections.expected && (
                    <p className="trd-detail-section-body">{selectedTC.expectedResult}</p>
                  )}
                </div>
              )}

              {/* ── Result recording panel — collapsible ── */}
              {canEdit ? (
                <div className="trd-result-panel">
                  <button className="trd-section-toggle trd-section-toggle-result" onClick={() => toggleSection('result')}>
                    <span className="trd-section-toggle-arrow">{openSections.result ? '▼' : '▶'}</span>
                    <span className="trd-section-toggle-label">RECORD RESULT</span>
                    {!openSections.result && (
                      <span className="trd-section-toggle-status-pill" style={{
                        background: (STATUS_META[editStatus] || STATUS_META.not_started).bg,
                        color: (STATUS_META[editStatus] || STATUS_META.not_started).color,
                        border: `1px solid ${(STATUS_META[editStatus] || STATUS_META.not_started).color}44`,
                      }}>
                        {(STATUS_META[editStatus] || STATUS_META.not_started).icon}{' '}
                        {(STATUS_META[editStatus] || STATUS_META.not_started).label}
                      </span>
                    )}
                  </button>

                  {openSections.result && (
                    <>
                      <div className="trd-status-buttons">
                        {STATUS_OPTIONS.map(s => {
                          const m = STATUS_META[s];
                          const isActive = editStatus === s;
                          return (
                            <button key={s}
                              className={`trd-status-btn ${isActive ? 'active' : ''}`}
                              style={isActive ? { background: m.bg, color: m.color, borderColor: m.color } : {}}
                              onClick={() => setEditStatus(s)}>
                              <span>{m.icon}</span> {m.label}
                            </button>
                          );
                        })}
                      </div>

                      <div className="trd-result-fields">
                        <div className="trd-result-field">
                          <label>Comment</label>
                          <textarea className="form-control form-control-sm" rows={2}
                            value={editComment}
                            onChange={e => setEditComment(e.target.value)}
                            placeholder="Notes about this result…" />
                        </div>
                        <div className="trd-result-field">
                          <label>
                            Bug ID
                            {project?.jiraIntegration?.jiraUrl && (
                              <span className="trd-field-hint"> (JIRA issue key)</span>
                            )}
                          </label>
                          <div className="input-group input-group-sm">
                            <input className="form-control"
                              value={editBugId}
                              onChange={e => setEditBugId(e.target.value)}
                              placeholder={project?.jiraIntegration?.projectKey
                                ? `${project.jiraIntegration.projectKey}-123`
                                : 'e.g. BUG-42'} />
                            {editBugId && getBugUrl(editBugId) && (
                              <a className="btn btn-outline-secondary" href={getBugUrl(editBugId)}
                                target="_blank" rel="noopener noreferrer" title="Open in JIRA">🔗</a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="trd-result-actions">
                        <StatusBadge status={editStatus} size="lg" />
                        <button className="btn btn-primary trd-save-btn"
                          onClick={saveResult} disabled={saving}>
                          {saving
                            ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                            : <>✓ Save & Next</>}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Read-only result for completed/aborted runs */
                selectedResult?.status && selectedResult.status !== 'not_started' && (
                  <div className="trd-result-panel trd-result-readonly">
                    <button className="trd-section-toggle" onClick={() => toggleSection('result')}>
                      <span className="trd-section-toggle-arrow">{openSections.result ? '▼' : '▶'}</span>
                      <span className="trd-section-toggle-label">RESULT</span>
                    </button>
                    {openSections.result && (
                      <div className="mt-2">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <StatusBadge status={selectedResult.status} size="lg" />
                          {(selectedResult.bugId || selectedResult.jiraIssueKey) && (
                            <span className="trd-bug-badge">
                              🐛 {getBugUrl(selectedResult.bugId || selectedResult.jiraIssueKey)
                                ? <a href={getBugUrl(selectedResult.bugId || selectedResult.jiraIssueKey)}
                                    target="_blank" rel="noopener noreferrer">
                                    {selectedResult.bugId || selectedResult.jiraIssueKey}
                                  </a>
                                : (selectedResult.bugId || selectedResult.jiraIssueKey)}
                            </span>
                          )}
                          {selectedResult.executedBy?.username && (
                            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                              by {selectedResult.executedBy.username}
                            </span>
                          )}
                        </div>
                        {selectedResult.comment && (
                          <p className="mt-2 mb-0 text-muted" style={{ fontSize: '0.88rem' }}>{selectedResult.comment}</p>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Run Modal ── */}
      {showEditModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">✏️ Edit Test Run</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)} disabled={editRunSaving} />
              </div>
              <div className="modal-body">
                {editRunError && <div className="alert alert-danger py-2">{editRunError}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Run Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={editRunData.name}
                      onChange={e => setEditRunData(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea className="form-control" rows={2} value={editRunData.description}
                      onChange={e => setEditRunData(p => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Assigned To</label>
                    {editRunUsers.length > 0 ? (
                      <select
                        className="form-select"
                        value={editRunData.assignedTo || ''}
                        onChange={e => setEditRunData(p => ({ ...p, assignedTo: e.target.value }))}
                      >
                        <option value="">— Unassigned —</option>
                        {editRunUsers.map(u => (
                          <option key={u._id || u.id} value={u._id || u.id}>
                            {u.username}{u.email ? ` (${u.email})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control" value={editRunData.assignedTo}
                        onChange={e => setEditRunData(p => ({ ...p, assignedTo: e.target.value }))}
                        placeholder="Loading users…" />
                    )}
                  </div>
                </div>
                <hr className="my-3" />
                <p className="fw-semibold mb-2">⚙️ Configuration</p>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Platform</label>
                    <select className="form-select" value={editRunData.platform}
                      onChange={e => setEditRunData(p => ({ ...p, platform: e.target.value }))}>
                      <option value="">— select —</option>
                      {['Web', 'iOS', 'Android', 'Windows', 'macOS', 'Linux', 'API'].map(v =>
                        <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Environment</label>
                    <select className="form-select" value={editRunData.environment}
                      onChange={e => setEditRunData(p => ({ ...p, environment: e.target.value }))}>
                      <option value="">— select —</option>
                      {['Development', 'Staging', 'UAT', 'Production', 'QA'].map(v =>
                        <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">App Version</label>
                    <input className="form-control" placeholder="e.g. 2.4.1" value={editRunData.appVersion}
                      onChange={e => setEditRunData(p => ({ ...p, appVersion: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Build Number</label>
                    <input className="form-control" placeholder="e.g. 301" value={editRunData.buildNumber}
                      onChange={e => setEditRunData(p => ({ ...p, buildNumber: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Device / Browser</label>
                    <input className="form-control" placeholder="e.g. iPhone 15 Pro" value={editRunData.deviceName}
                      onChange={e => setEditRunData(p => ({ ...p, deviceName: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">OS Version</label>
                    <input className="form-control" placeholder="e.g. iOS 17.2" value={editRunData.osVersion}
                      onChange={e => setEditRunData(p => ({ ...p, osVersion: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Browser</label>
                    <input className="form-control" placeholder="e.g. Chrome" value={editRunData.browserName}
                      onChange={e => setEditRunData(p => ({ ...p, browserName: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Browser Version</label>
                    <input className="form-control" placeholder="e.g. 122" value={editRunData.browserVersion}
                      onChange={e => setEditRunData(p => ({ ...p, browserVersion: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} placeholder="Additional notes..." value={editRunData.notes}
                      onChange={e => setEditRunData(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={editRunSaving}>Cancel</button>
                <button className="btn btn-primary" onClick={saveEditRun} disabled={editRunSaving}>
                  {editRunSaving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : '💾 Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}